import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type {
  MachineDocumentResponse,
  MachineDowntimeResponse,
  MachineResponse,
  MaintenanceJobResponse,
  ServiceResponse,
  SetMachineServicesRequest,
} from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { PanelHeading } from '../money/PanelHeading';
import { ErrorState } from '../../components/states/StateViews';
import { useLocalisedName } from '../../i18n/localisedName';
import { money } from '../catalog/money';
import { clinicDay } from '../scheduling/clinicTime';

/**
 * One machine, in full: what it can deliver, what has been done to it, and its paperwork.
 *
 * The compatibility list is the part with teeth. Ticking a treatment here means a slot for it can
 * only be offered when one of its machines is up (FR-806, FR-301) — so the panel says so in words
 * rather than leaving somebody to discover it when Thursday's diary empties.
 */
export function MachineDetailPanel({
  clinicId,
  machineId,
  services,
  canManage,
  invalidates,
}: {
  readonly clinicId: string;
  readonly machineId: string;
  readonly services: readonly ServiceResponse[];
  readonly canManage: boolean;
  readonly invalidates: readonly (readonly unknown[])[];
}) {
  const { t } = useTranslation();
  const { name } = useLocalisedName();

  const machine = useApiQuery<MachineResponse>(
    queryKeys.machine(clinicId, machineId),
    `/clinics/${clinicId}/machines/${machineId}`,
  );
  const downtime = useApiQuery<MachineDowntimeResponse[]>(
    queryKeys.machineDowntime(clinicId, machineId),
    `/clinics/${clinicId}/machines/${machineId}/downtime`,
  );
  const history = useApiQuery<MaintenanceJobResponse[]>(
    queryKeys.maintenanceJobs(clinicId, `machine:${machineId}`),
    `/clinics/${clinicId}/machines/maintenance?machineId=${machineId}`,
  );
  const documents = useApiQuery<MachineDocumentResponse[]>(
    queryKeys.machineDocuments(clinicId, machineId),
    `/clinics/${clinicId}/machines/${machineId}/documents`,
  );

  const setServices = useApiMutation<MachineResponse, SetMachineServicesRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/machines/${machineId}/services`,
      options: { method: 'PUT', body },
    }),
    { invalidates: [...invalidates, queryKeys.machine(clinicId, machineId)] },
  );

  return (
    <AsyncSection query={machine}>
      {(detail) => (
        <section className="panel">
          <PanelHeading
            title={name(detail, detail.code ?? '')}
            hint={[detail.manufacturer, detail.model, detail.serialNumber]
              .filter(Boolean)
              .join(' · ')}
          />

          <h3>{t('machines.treatments')}</h3>
          <p className="muted">{t('machines.treatmentsHint')}</p>
          <form
            className="checkbox-grid"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              setServices.mutate({ serviceIds: form.getAll('serviceIds').map(String) });
            }}
          >
            {services.map((service) => (
              <label key={service.id} className="field checkbox">
                <input
                  type="checkbox"
                  name="serviceIds"
                  value={service.id}
                  defaultChecked={detail.compatibleServices?.some(
                    (mapped) => mapped.serviceId === service.id,
                  )}
                  disabled={!canManage}
                />
                <span>{name(service, service.code ?? '')}</span>
              </label>
            ))}
            {setServices.isError ? <ErrorState error={setServices.error} /> : null}
            {canManage ? (
              <button type="submit" className="primary" disabled={setServices.isPending}>
                {t('common.save')}
              </button>
            ) : null}
          </form>

          <h3>{t('machines.downtimeHistory')}</h3>
          {(downtime.data ?? []).length === 0 ? (
            <p className="muted">{t('machines.noDowntime')}</p>
          ) : (
            <ul className="totals-list">
              {(downtime.data ?? []).map((period) => (
                <li key={period.id}>
                  <span className={`pill pill--${(period.kind ?? '').toLowerCase()}`}>
                    {t(`machines.downtimeKinds.${period.kind}`)}
                  </span>
                  <span>
                    {period.fromAt ? clinicDay(period.fromAt) : ''} →{' '}
                    {period.untilAt ? clinicDay(period.untilAt) : t('machines.stillDown')}
                  </span>
                  <span className="muted">{period.reason}</span>
                </li>
              ))}
            </ul>
          )}

          <h3>{t('machines.serviceHistory')}</h3>
          {(history.data ?? []).length === 0 ? (
            <p className="muted">{t('machines.noJobs')}</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('machines.jobKind')}</th>
                    <th>{t('machines.jobTitle')}</th>
                    <th>{t('machines.vendor')}</th>
                    <th>{t('machines.cost')}</th>
                    <th>{t('sales.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(history.data ?? []).map((job) => (
                    <tr key={job.id}>
                      <td>{t(`machines.jobKinds.${job.kind}`)}</td>
                      <td>{job.title}</td>
                      <td className="muted">{job.vendor ?? '—'}</td>
                      <td>{job.cost ? money(job.cost) : '—'}</td>
                      <td>
                        <span className={`pill pill--${(job.status ?? '').toLowerCase()}`}>
                          {t(`machines.jobStatuses.${job.status}`)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3>{t('machines.documents')}</h3>
          {(documents.data ?? []).length === 0 ? (
            <p className="muted">{t('machines.noDocuments')}</p>
          ) : (
            <ul className="totals-list">
              {(documents.data ?? []).map((document) => (
                <li key={document.id}>
                  <span className="chip">{t(`machines.documentKinds.${document.kind}`)}</span>
                  <span>{document.title}</span>
                  <span className="muted">{document.validUntil ?? ''}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </AsyncSection>
  );
}
